import Vue from 'vue';
import VueRouter from 'vue-router';
import { apiAuth, appMng, apiBilling } from '../main';
import {
	AuthError,
	InvalidSessionError,
	NoAccessTokenError,
	TokenExpiredError,
	TokenRefreshError,
	UserData,
} from '../api/api-auth';
import notify, {
	NOTIFICATION_TYPES,
	NotificationOptions,
} from '../helpers/notify';
import dialog, { DialogOptions } from '../helpers/dialogs';
import { GET_USER_DATA } from '../store/modules/user';
import store from '../store';
import { isEqual } from 'lodash/lang';

//Components
const Layout = () => import('../components/Layout');
const Teste = () => import('../views/Teste');
const MyAccount = () => import('../views/User/MyAccount');
const Plans = () => import('../views/Plans/Plans');
const PaymentStepper = () => import('../views/Payment/PaymentStepper');
const PostPayment = () => import('../views/Payment/PostPayment');
const NotFound = () => import('../views/Errors/NotFound');
const InternalServerError = () => import('../views/Errors/InternalServerError');
const Unauthorized = () => import('../views/Errors/Unauthorized');
const ProcessingList = () => import('../views/Processings/ProcessingList');
const ProcessingStepper = () =>
	import('../views/Processings/Configuration/ProcessingStepper');
const MapView = () => import('../views/MapView');
const MapViewShared = () => import('../views/MapViewShared');
const OrderProcessingMapView = () => import('../views/OrderProcessingMapView');
const RedirectAuth = () => import('../views/RedirectAuth');
const Login = () => import('../views/Login');
const Register = () => import('../views/Register');
const ProcessingShare = () => import('../views/Processings/ProcessingShare');
const ProjectShare = () => import('../views/Projects/ProjectShare');
const UnsubscribeEmail = () => import('../views/UnsubscribeEmail');
const ProductsPayment = () => import('../views/Payment/ProductsPayment');
const AccreditedRegister = () =>
	import('../views/FlightProvider/AccreditedRegister');
const AccreditationSignature = () =>
	import('../views/FlightProvider/AccreditationSignature');
const AccreditedTermsUse = () =>
	import('../views/FlightProvider/AccreditedTermsUse');
const CreateManualUpload = () =>
	import('../views/AdminOnly/CreateManualUpload');
const ManualUpload = () => import('../views/ManualUpload');
const News = () => import('../views/AdminOnly/News');
const NewsManager = () => import('../views/AdminOnly/NewsManager');
const ListNews = () => import('../views/News/ListNews');

Vue.use(VueRouter);

const generateMeta = (requireLogin, autoResolveLoading, gtm) => {
	return {
		requireLogin: requireLogin,
		autoResolveLoading: autoResolveLoading,
		gtm: gtm,
	};
};

const routes = [
	{
		path: '/app',
		name: 'app',
		component: Layout,
		redirect:
			process.env.NODE_ENV === 'production'
				? '/app/processing-list'
				: '/app/processing-list',
		children: [
			{
				path: 'testes',
				name: 'testes',
				component: Teste,
				redirect:
					process.env.NODE_ENV === 'production' ? '/404' : undefined,
			},
			{
				path: 'processing-list',
				name: 'processing-list',
				component: ProcessingList,
				meta: generateMeta(true, true, 'ProcessingList'),
			},
			{
				path: 'processing',
				name: 'processing',
				component: ProcessingStepper,
				meta: generateMeta(true, false, 'ProcessingStepper'),
			},
			{
				path: 'my-account',
				name: 'my-account',
				component: MyAccount,
				meta: generateMeta(true, true, 'MyAccount'),
			},
			{
				path: 'plans',
				name: 'plans',
				component: Plans,
				meta: generateMeta(true, false, 'Plans'),
			},
			{
				path: 'payment',
				name: 'payment',
				component: PaymentStepper,
				meta: generateMeta(true, true, 'Payment'),
			},
			{
				path: 'post-payment',
				name: 'post-payment',
				component: PostPayment,
				meta: generateMeta(true, true, 'PostPayment'),
			},
			{
				path: 'accredited-register',
				name: 'accredited-register',
				component: AccreditedRegister,
				meta: generateMeta(true, true, 'AccreditedRegister'),
			},
			{
				path: 'accreditation-signature',
				name: 'accreditation-signature',
				component: AccreditationSignature,
				meta: generateMeta(true, true, 'AccreditationSignature'),
			},
			{
				path: 'manual-uploads',
				name: 'manual-uploads',
				component: CreateManualUpload,
				meta: generateMeta(true, true, 'CreateManualUpload'),
			},
			{
				path: 'news',
				name: 'news',
				component: News,
				meta: generateMeta(true, true, 'News'),
			},
			{
				path: 'manager-news',
				name: 'manager-news',
				component: NewsManager,
				meta: generateMeta(true, true, 'NewsManager'),
			},
			{
				path: 'list-news',
				name: 'list-news',
				component: ListNews,
				meta: generateMeta(true, true, 'ListNews'),
			},
		],
	},
	// App without Layout
	{
		path: '/app/processing/map/:id',
		name: 'map',
		component: MapView,
		meta: generateMeta(true, false, 'MapView'),
	},
	{
		path: '/app/processing/share/:token',
		name: 'map-share',
		component: MapViewShared,
		meta: generateMeta(false, false, 'MapView'),
	},
	{
		path: '/app/order-processing/map/:id',
		name: 'map-order',
		component: OrderProcessingMapView,
		meta: generateMeta(true, false, 'MapView'),
	},
	{
		path: '/app/processing-share/:token',
		name: 'processing-share',
		component: ProcessingShare,
		meta: generateMeta(true, true, 'ProcessingShare'),
	},
	{
		path: '/app/project-share/:token',
		name: 'project-share',
		component: ProjectShare,
		meta: generateMeta(true, true, 'ProjectShare'),
	},
	{
		path: '/app/products-payment/:product/:name?',
		name: 'products-payment',
		component: ProductsPayment,
		meta: generateMeta(true, true, 'ProductsPayment'),
	},
	{
		path: '/unsubscribe-email/',
		name: 'unsubscribe-email',
		component: UnsubscribeEmail,
		meta: generateMeta(false, true, 'UnsubscribeEmail'),
	},
	{
		path: '/manual-upload/:id',
		name: 'manual-upload',
		component: ManualUpload,
		meta: generateMeta(false, true, 'ManualUpload'),
	},
	// Others
	{
		path: '/redirect-login',
		name: 'redirect-login',
		component: RedirectAuth,
		meta: generateMeta(false, false, 'RedirectLogin'),
	},
	{
		path: '/login',
		name: 'login',
		component: Login,
		meta: generateMeta(false, false, 'Login'),
	},
	{
		path: '/register/:plan?/:email?',
		name: 'register',
		component: Register,
		meta: generateMeta(false, false, 'Register'),
	},
	{
		path: '/accredited-terms-use',
		name: 'accredited-terms-use',
		component: AccreditedTermsUse,
		meta: generateMeta(false, false, 'AccreditedTermsUse'),
	},
	// Root route
	{
		path: '/',
		name: 'root',
		redirect: '/app',
	},
	//Error routes
	{ path: '/404', component: NotFound },
	{ path: '/500', component: InternalServerError },
	{ path: '/401', component: Unauthorized },
	{ path: '*', redirect: '/404' },
];

const router = new VueRouter({
	mode: 'history',
	base: process.env.BASE_URL,
	routes,
});

router.afterEach((to) => {
	if (to.meta['requireLogin']) {
		appMng.setIsLoadingPage(true);
		if (!apiAuth.isSetup) {
			apiAuth.setUp();
		}
		const autoResolveLoading = to.meta['autoResolveLoading'];
		verifyLogin()
			.then(() => {
				onLoginVerificationSuccess(autoResolveLoading);
			})
			.catch((err) => {
				onLoginVerificationError(to, err);
			});
	}
});

async function verifyLogin() {
	// Vê se tem algum token de acesso guardado na memoria
	let authToken = null;
	try {
		authToken = apiAuth.getAuthToken(false);
	} catch (e) {
		throw e;
	}
	if (!apiBilling.isSetup) {
		apiBilling.setUp(authToken);
	}
	// Se tiver valida os tokens
	try {
		await apiAuth.oauthIntrospectToken();
	} catch (e) {
		throw e;
	}

	try {
		await apiAuth.verifyAuthSession();
	} catch (e) {
		throw e;
	}

	apiBilling
		.checkBillingSignature(authToken)
		.then((res) => {
			if (!res) {
				appMng.setIsSessionExpired(
					'Seu plano expirou! Entre em contato com suporte@mappa.ag para ativar seu plano e ter acesso aos seus dados novamente',
				);
				apiAuth.logout();
			}
		})
		.catch(() => {
			appMng.setIsSessionExpired(
				'Seu plano expirou! Entre em contato com suporte@mappa.ag para ativar seu plano e ter acesso aos seus dados novamente',
			);
			apiAuth.logout();
		});

	return authToken;
}

function onLoginVerificationSuccess(autoResolveLoading) {
	const userData = store.getters[GET_USER_DATA];
	if (!userData || !userData.blEmailValidated) {
		apiAuth
			.whoami()
			.then((response) => {
				const userData = UserData.fromObject(response.data);

				appMng.setIsIntl(!isEqual(userData['abCountry'], 'BR'));

				if (userData.blEmailValidated) {
					if (autoResolveLoading) {
						appMng.setIsLoadingPage(false);
					}
					return;
				}

				dialog(
					new DialogOptions({
						title: 'Verificação de e-mail',
						persistent: true,
						content:
							'Sua conta ainda não foi verificada, por favor acesse seu e-mail e confirme a verificação para continuar na plataforma',
						cancelText: 'Ok, entendi',
						confirmText: 'Reenviar e-mail',
						onConfirm: () => {
							apiAuth
								.requestResendToken(userData.strEmail)
								.then(() => {
									notify(
										new NotificationOptions({
											message:
												'Verificação de e-mail enviada com sucesso, verifique seu e-mail.',
											type: NOTIFICATION_TYPES.SUCCESS,
										}),
									);
									router.push('/login');
								})
								.catch(() => {
									router.push('/login');
								});
						},
						onCancel: () => {
							apiAuth
								.logout()
								.then(() => {
									router.push('/login');
								})
								.catch(() => {
									router.push('/login');
								});
						},
					}),
				);
			})
			.catch(() => {
				dialog(
					new DialogOptions({
						title: 'Verificação de e-mail',
						persistent: true,
						content:
							'Sua conta ainda não foi verificada, por favor acesse seu e-mail e confirme a verificação para continuar na plataforma',
						cancelText: 'Ok, entendi',
						confirmText: 'Reenviar e-mail',
						onConfirm: () => {
							apiAuth
								.requestResendToken(userData.strEmail)
								.then(() => {
									notify(
										new NotificationOptions({
											message:
												'Verificação de e-mail enviada com sucesso, verifique seu e-mail.',
											type: NOTIFICATION_TYPES.SUCCESS,
										}),
									);
									router.push('/login');
								})
								.catch(() => {
									router.push('/login');
								});
						},
						onCancel: () => {
							apiAuth
								.logout()
								.then(() => {
									router.push('/login');
								})
								.catch(() => {
									router.push('/login');
								});
						},
					}),
				);
			});
	} else if (autoResolveLoading) {
		const isIntl = !isEqual(userData['abCountry'], 'BR');
		appMng.setIsIntl(isIntl);
		appMng.setIsLoadingPage(false);
	} else {
		appMng.setIsIntl(!isEqual(userData['abCountry'], 'BR'));
	}
}

function onLoginVerificationError(to, err) {
	if (err instanceof NoAccessTokenError) {
		router.push('/login', () => {});
		return;
	}

	if (
		err instanceof InvalidSessionError ||
		err instanceof TokenRefreshError
	) {
		appMng.setIsSessionExpired();
		return;
	}

	apiAuth
		.oauthRefreshToken()
		.then(() => {
			const autoResolveLoading = to.meta['autoResolveLoading'];
			router
				.replace(to.fullPath, () => {})
				.then(() => {
					onLoginVerificationSuccess(autoResolveLoading);
				});
		})
		.catch(() => {
			if (err instanceof TokenExpiredError || err instanceof AuthError) {
				appMng.setIsSessionExpired(
					'Você não parece estar logado na plataforma, por favor faço o login novamente.',
				);
			} else {
				console.debug('Unhandled exception: ');
				console.debug(err);
				notify(
					new NotificationOptions({
						message:
							'Você parece estar sem conexão com a internet, por favor tente novamente mais tarde',
						type: NOTIFICATION_TYPES.ERROR,
					}),
				);
			}

			appMng.setIsLoadingPage(false);
		});
}

export default router;
